import { ReactElement } from 'react';

import { InteractionStatus } from '@dreryk/drerykweb-medicine-api';

import {
   assertUnreachable,
   createSingleInterpolatedTranslationKey,
   createTranslatedText,
   stringFormatter,
   TranslatableText,
   useTranslate,
} from '@dreryk/react-common';
import {
   AlcoholIcon,
   BreastFeedingIcon,
   CarIcon,
   IconFillColor,
   IconWrapper,
   PregnantFirstTrimesterIcon,
   PregnantSecondTrimesterIcon,
   PregnantThirdTrimesterIcon,
   StyledFlex,
   Tooltip,
   TooltipLabel,
} from '@dreryk/react-components';

import { MedicineSafetyIndicatorType, useMedicineSafetyIndicatorConfig } from '~libs.shared.medicine';

import { translationKeys } from '../../translations';

const resolveCommentForSafeStatusByType = (type: MedicineSafetyIndicatorType): TranslatableText => {
   switch (type) {
      case 'drivingCar':
         return translationKeys.safetyIndicators.drivingCarSafe;
      case 'interactionWithAlcohol':
         return translationKeys.safetyIndicators.noAlcoholInteraction;
      case 'breastFeeding':
         return translationKeys.safetyIndicators.applyingAllowed;
      case 'pregnantFirstTrimester':
         return translationKeys.safetyIndicators.applyingAllowed;
      case 'pregnantSecondTrimester':
         return translationKeys.safetyIndicators.applyingAllowed;
      case 'pregnantThirdTrimester':
         return translationKeys.safetyIndicators.applyingAllowed;
      default:
         return assertUnreachable(type);
   }
};

const resolveCommentForAmbiguousStatus = (comment?: string): TranslatableText => {
   if (!comment) {
      return translationKeys.safetyIndicators.couldNotResolveCategory;
   }
   return createSingleInterpolatedTranslationKey(
      translationKeys.safetyIndicators.couldNotResolveCategoryForName,
      'name',
      comment
   );
};

const resolveCommentForUnknownStatus = (comment?: string): TranslatableText => {
   if (!comment) {
      return translationKeys.safetyIndicators.noData;
   }
   return createTranslatedText(comment);
};

const resolveComment = (
   status: InteractionStatus,
   type: MedicineSafetyIndicatorType,
   comment?: string
): TranslatableText => {
   switch (status) {
      case 'SAFE':
         return resolveCommentForSafeStatusByType(type);
      case 'AMBIGUOUS':
         return resolveCommentForAmbiguousStatus(comment);
      case 'UNKNOWN':
         return resolveCommentForUnknownStatus(comment);
      default:
         return createTranslatedText(comment!);
   }
};

const resolveIconByIndicatorType = (type: MedicineSafetyIndicatorType): ReactElement => {
   switch (type) {
      case 'drivingCar':
         return <CarIcon />;
      case 'interactionWithAlcohol':
         return <AlcoholIcon />;
      case 'breastFeeding':
         return <BreastFeedingIcon />;
      case 'pregnantFirstTrimester':
         return <PregnantFirstTrimesterIcon />;
      case 'pregnantSecondTrimester':
         return <PregnantSecondTrimesterIcon />;
      case 'pregnantThirdTrimester':
         return <PregnantThirdTrimesterIcon />;
      default:
         return assertUnreachable(type);
   }
};

const resolveIconFillColorByIndicatorStatus = (status: InteractionStatus): IconFillColor => {
   switch (status) {
      case 'SAFE':
         return IconFillColor.SUCCESS;
      case 'WARNING':
         return IconFillColor.WARNING;
      case 'UNSAFE':
         return IconFillColor.ERROR;
      case 'AMBIGUOUS':
         return IconFillColor.INFO;
      case 'UNKNOWN':
         return IconFillColor.BACKGROUND;
      default:
         return assertUnreachable(status);
   }
};

type MedicineSafetyIndicatorIconProps = {
   readonly indicatorType: MedicineSafetyIndicatorType;
   readonly medicineId: number;
};

export const MedicineSafetyIndicatorIcon = (props: MedicineSafetyIndicatorIconProps): ReactElement | null => {
   const config = useMedicineSafetyIndicatorConfig({
      medicineId: props.medicineId,
      indicatorType: props.indicatorType,
   });

   const translate = useTranslate();

   if (!config) {
      return null;
   }

   return (
      <Tooltip
         testId={props.indicatorType}
         content={() => (
            <div>
               <TooltipLabel label={translationKeys.safetyIndicators[props.indicatorType]} />
               <StyledFlex gap={'4px'} direction={'column'}>
                  <span>
                     {stringFormatter(translate(resolveComment(config.status, props.indicatorType, config.comment)), {
                        options: { capitalizeFirstLetter: true },
                        translator: translate,
                     })}
                  </span>
                  {'interactionTarget' in config ? (
                     <span>
                        {translate(
                           createSingleInterpolatedTranslationKey(
                              translationKeys.safetyIndicators.influenceOn,
                              'interactionTarget',
                              config.interactionTarget
                           )
                        )}
                     </span>
                  ) : null}
               </StyledFlex>
            </div>
         )}
      >
         <IconWrapper testId={props.indicatorType} fillColor={resolveIconFillColorByIndicatorStatus(config.status)}>
            {resolveIconByIndicatorType(props.indicatorType)}
         </IconWrapper>
      </Tooltip>
   );
};
